import type { ContingentTariffOption } from '../../lib/api/billing';
import { CONTINGENT_MODE_LABEL, formatCents, installmentLabel, tariffPriceLabel } from '../../lib/billing';
import { cn } from '../../lib/utils';

interface Props {
  tariffs: ContingentTariffOption[];
  selectedId?: number;
  onSelect: (tariffId: number) => void;
  selectionLabel: string;
}

/**
 * Anmelde-Wizard, Schritt „Pakete“ beim Kontingent mit Tarif (ADR-0003, Phase 5): Das gewählte
 * Paket wird gebucht, sobald die Anmeldung angenommen oder der Kurs gestartet wird, und ist
 * zugleich das Folgepaket.
 */
export default function ContingentTariffStep({ tariffs, selectedId, onSelect, selectionLabel }: Props) {
  return (
    <div className="space-y-4">
      <div className="rounded-lg border bg-muted/20 px-4 py-3 text-sm">
        Gewählt: <span className="text-foreground">{selectionLabel}</span>
      </div>

      <div className="rounded-lg bg-primary px-4 py-3 text-sm font-semibold text-primary-foreground">Paket</div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3" role="radiogroup" aria-label="Paket">
        {tariffs.map((tariff) => {
          const selected = tariff.id === selectedId;
          const mode = tariff.contingentMode ?? 'prepaid';
          const perUnit = tariff.units && tariff.units > 0 ? Math.round(tariff.priceCents / tariff.units) : null;
          return (
            <button
              key={tariff.id}
              type="button"
              role="radio"
              aria-checked={selected}
              onClick={() => onSelect(tariff.id)}
              className={cn(
                'rounded-lg border p-4 text-left text-sm transition-colors',
                selected ? 'border-primary bg-primary/10' : 'hover:bg-muted/40',
              )}
            >
              <span className="block font-medium">{tariff.name}</span>
              <span className="block text-muted-foreground">{CONTINGENT_MODE_LABEL[mode]}</span>
              <span className="mt-2 block tabular-nums">{tariffPriceLabel(tariff)}</span>
              {mode === 'installments' && tariff.installmentCount ? (
                <span className="block tabular-nums text-muted-foreground">
                  {installmentLabel(tariff.priceCents, tariff.installmentCount)} monatlich
                </span>
              ) : null}
              {mode !== 'usage_based' && perUnit !== null ? (
                <span className="block tabular-nums text-muted-foreground">{formatCents(perUnit)} je Einheit</span>
              ) : null}
            </button>
          );
        })}
      </div>

      <p className="text-sm text-muted-foreground">
        Durchgeführte Stunden und unentschuldigtes Fehlen verbrauchen eine Einheit. Das Paket wird gebucht, sobald die
        Anmeldung angenommen oder der Kurs gestartet ist; sind die Einheiten aufgebraucht, folgt es automatisch erneut,
        solange der Vertrag nicht gekündigt ist.
      </p>
    </div>
  );
}
